import { useState } from 'react';

import { useMutation } from '@apollo/client';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';

import { navigate, routes } from '@redwoodjs/router';

import { ConfirmationModal } from 'src/components/Modals';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import {
    CANCEL_MERCHANT_APPLICATION,
    CONTINUE_TRIAL_MERCHANT_SERVICE,
} from 'src/graphql/queries/merchantApplications.query';

type Props = {
    serviceId: number;
    legalName: undefined | string;
    applicationId: undefined | number;
};

export default function TrialMerchantDashboardActions({
    serviceId,
    legalName,
    applicationId,
}: Readonly<Props>) {
    const [openCancelServiceModal, setOpenCancelServiceModal] = useState<boolean>(false);
    const [openContinueServiceModal, setOpenContinueServiceModal] = useState<boolean>(false);

    const [cancelApplication] = useMutation(CANCEL_MERCHANT_APPLICATION);

    const [continueServiceMutation] = useMutation(CONTINUE_TRIAL_MERCHANT_SERVICE);

    const cancelService = async () => {
        try {
            await cancelApplication({ variables: { applicationIds: [applicationId] } });

            showSuccessToast(`Trial service has been cancelled for "${legalName}".`);

            return navigate(routes.trialMerchants());
        } catch (error) {
            showErrorToast('Failed to cancel the trial service. Please try again.');
        }
    };

    const continueService = async () => {
        try {
            await continueServiceMutation({ variables: { input: { serviceId } } });

            showSuccessToast(`The service has been continued for "${legalName}".`);

            return navigate(routes.trialMerchants());
        } catch (e) {
            showErrorToast(e?.message);
            setOpenContinueServiceModal(false);
        }
    };

    return (
        <>
            {legalName !== undefined && (
                <>
                    <Button
                        variant="npi-success"
                        onClick={() => {
                            setOpenContinueServiceModal(true);
                        }}
                        disableElevation
                    >
                        Continue Service
                    </Button>

                    <Button
                        variant="npi-danger"
                        onClick={() => {
                            setOpenCancelServiceModal(true);
                        }}
                        disableElevation
                    >
                        Cancel Trial
                    </Button>
                </>
            )}

            <Button
                startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                variant="npi-primary"
                onClick={() => {
                    return navigate(routes.trialMerchants());
                }}
                disableElevation
            >
                Back to Trial Merchants
            </Button>

            <ConfirmationModal
                isOpen={openCancelServiceModal}
                title="Cancel Trial"
                message={`Are you sure you want to cancel this trial service for ${legalName}?`}
                confirmText="Yes"
                cancelText="No"
                onCancel={() => {
                    setOpenCancelServiceModal(false);
                }}
                onConfirm={async () => {
                    await cancelService();
                }}
            />

            <ConfirmationModal
                isOpen={openContinueServiceModal}
                title="Continue Service"
                message={`Are you sure you want to continue the service for ${legalName}?`}
                confirmText="Yes"
                cancelText="No"
                onCancel={() => {
                    setOpenContinueServiceModal(false);
                }}
                onConfirm={async () => {
                    await continueService();
                }}
            />
        </>
    );
}
